import api from '../../services/api';

export interface WarehouseCorrectionSummary {
  id: string;
  createdAt: string;
  createdByName: string | null;
  reason: string;
  // PZ storno (2026-09-18): distinguishes a storno row from an ordinary
  // correction. WZ correction rows never carry it — treat absence as
  // 'CORRECTION', never render the storno tag for an undefined kind.
  kind?: 'CORRECTION' | 'STORNO';
}

export async function loadWarehouseCorrections(
  documentType: 'PZ' | 'WZ',
  id: string
): Promise<WarehouseCorrectionSummary[]> {
  const { data } = await api.get(
    `/${documentType.toLowerCase()}/${encodeURIComponent(id)}/corrections`
  );
  return Array.isArray(data) ? data : [];
}

export async function saveWZCorrection(
  id: string,
  payload: Record<string, unknown>,
  confirm: (warning: any) => Promise<boolean>
): Promise<boolean> {
  try {
    await api.put(`/wz/${encodeURIComponent(id)}`, payload);
  } catch (error: any) {
    const warning = error?.response?.data;
    if (warning?.code !== 'WZ_RELEASE_EXCEEDS_ORDERED_QUANTITY') throw error;
    if (!(await confirm(warning))) return false;
    await api.put(`/wz/${encodeURIComponent(id)}`, {
      ...payload,
      confirmExceedsOrderedQuantity: true,
      confirmationFingerprint: warning.confirmationFingerprint,
    });
  }
  return true;
}

export interface PZCorrectionReceiptDatePreview {
  currency: string;
  currentReceiptDate: string | null;
  currentExchangeRateToPLN: number | null;
  receiptDate: string;
  receiptDateChanged: boolean;
  exchangeRate: {
    rate: number | null;
    source: string | null;
    tableNumber: string | null;
    effectiveDate: string | null;
  };
  /** Customer orders whose consumed cost would change — numbers only, never money. */
  affectedOrders: Array<{ id: string; orderNumber: string; settled: boolean }>;
}

/**
 * Backend-computed answer for the PZ correction form (GET /pz/:id/correction-preview):
 * the NBP rate a new receipt date would carry and the orders it would revalue.
 * Owner decision 2026-09-18: receipt date = exchange-rate date.
 */
export async function loadPZCorrectionReceiptDatePreview(
  id: string,
  receiptDate: string,
): Promise<PZCorrectionReceiptDatePreview> {
  const { data } = await api.get(
    `/pz/${encodeURIComponent(id)}/correction-preview`,
    { params: { receiptDate } },
  );
  const rate = Number(data?.exchangeRate?.rate);
  return {
    currency: typeof data?.currency === 'string' ? data.currency : 'PLN',
    currentReceiptDate: typeof data?.currentReceiptDate === 'string' ? data.currentReceiptDate : null,
    currentExchangeRateToPLN: Number.isFinite(Number(data?.currentExchangeRateToPLN)) && data?.currentExchangeRateToPLN != null
      ? Number(data.currentExchangeRateToPLN) : null,
    receiptDate: typeof data?.receiptDate === 'string' ? data.receiptDate : receiptDate,
    receiptDateChanged: data?.receiptDateChanged === true,
    exchangeRate: {
      rate: Number.isFinite(rate) && rate > 0 ? rate : null,
      source: typeof data?.exchangeRate?.source === 'string' ? data.exchangeRate.source : null,
      tableNumber: typeof data?.exchangeRate?.tableNumber === 'string' ? data.exchangeRate.tableNumber : null,
      effectiveDate: typeof data?.exchangeRate?.effectiveDate === 'string' ? data.exchangeRate.effectiveDate : null,
    },
    affectedOrders: Array.isArray(data?.affectedOrders)
      ? data.affectedOrders
        .filter((row: any) => row && typeof row.id === 'string')
        .map((row: any) => ({
          id: row.id,
          orderNumber: typeof row.orderNumber === 'string' ? row.orderNumber : row.id,
          settled: row.settled === true,
        }))
      : [],
  };
}
